// online spreadsheet viewer — opens an .xlsx/.csv straight from R2 and renders it in the browser,
// so an order list (SHEET nn) or a per-vessel workbook can be read without downloading it. The file
// is fetched from the files API, parsed with SheetJS (readWorkbookGrid), and shown one sheet at a
// time with a text filter. Wide sheets scroll horizontally inside the modal body. On phones the viewer
// is a full-height sheet whose grid fills the body, with the first (SAP) column pinned while it scrolls.

import { useEffect, useMemo, useState } from 'react';
import { ErrorState, LoadingState, Modal, Segmented } from './ui.js';
import { useDateFormatter } from '../lib/format.js';
import { readWorkbookGrid, rowMatches, type WorkbookGrid } from '../features/orderList.js';

const MAX_ROWS = 2000; // guard against an enormous sheet locking the tab; a notice shows if hit

export function WorkbookViewerModal({
  fileKey,
  name,
  onClose,
}: {
  fileKey: string;
  name: string;
  onClose: () => void;
}) {
  const df = useDateFormatter();
  const [grid, setGrid] = useState<WorkbookGrid | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const [q, setQ] = useState('');

  useEffect(() => {
    const ctrl = new AbortController();
    let alive = true;
    setGrid(null);
    setError(null);
    setActive(0);
    setQ('');
    (async () => {
      try {
        const res = await fetch(`/api/files/download?key=${encodeURIComponent(fileKey)}`, {
          credentials: 'same-origin',
          signal: ctrl.signal,
        });
        if (!res.ok) throw new Error(`Could not load the file (${res.status}).`);
        const buf = await res.arrayBuffer();
        const XLSX: unknown = await import('xlsx');
        const parsed = readWorkbookGrid(XLSX, buf);
        if (!alive) return;
        if (parsed.sheets.length === 0) throw new Error('This workbook has no sheets to show.');
        setGrid(parsed);
      } catch (e) {
        if (!alive || ctrl.signal.aborted) return; // closed/re-opened - ignore the aborted fetch
        setError(e instanceof Error ? e.message : 'Could not open this file.');
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [fileKey]);

  const sheet = grid?.sheets[active] ?? null;
  const visible = useMemo(() => {
    if (!sheet) return [];
    const filtered = sheet.rows.filter((r) => rowMatches(r, q.trim()));
    return filtered.slice(0, MAX_ROWS);
  }, [sheet, q]);
  const totalRows = sheet?.rows.length ?? 0;
  const shown = visible.length;

  const cell: React.CSSProperties = { fontSize: 11, padding: '3px 7px', whiteSpace: 'nowrap' };

  return (
    <Modal
      title={`View - ${name}`}
      width={1120}
      className="wbv"
      onClose={onClose}
      footer={
        <>
          <a className="btn" href={`/api/files/download?key=${encodeURIComponent(fileKey)}`}>
            Download
          </a>
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {!grid && !error && <LoadingState label={`Opening ${name}…`} />}
      {error && <ErrorState error={new Error(error)} />}

      {grid && sheet && (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
            {grid.sheets.length > 1 && (
              <Segmented
                value={String(active)}
                onChange={(v) => { setActive(Number(v)); setQ(''); }}
                ariaLabel="Sheet"
                options={grid.sheets.map((s, i) => ({ value: String(i), label: s.name }))}
              />
            )}
            <input
              className="field"
              style={{ flex: '1 1 200px', minWidth: 160 }}
              placeholder="Filter rows…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              {q.trim() ? `${shown} of ${totalRows} rows` : `${totalRows} rows`}
            </span>
          </div>

          <div className="tbl-wrap is-grid wbv-grid">
            <table className="tbl">
              {sheet.header.length > 0 && (
                <thead>
                  <tr>
                    {sheet.header.map((h, i) => (
                      <th key={i} className={i === 0 ? 'pin-col' : undefined} style={{ ...cell, position: 'sticky', top: 0 }}>{h || ' '}</th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {visible.map((r, ri) => (
                  <tr key={ri}>
                    {r.map((c, ci) => (
                      <td key={ci} className={ci === 0 ? 'pin-col' : undefined} style={cell}>{df.maybe(c)}</td>
                    ))}
                  </tr>
                ))}
                {shown === 0 && (
                  <tr>
                    <td colSpan={Math.max(1, sheet.header.length)} className="muted" style={{ padding: 10 }}>
                      {totalRows === 0 ? 'This sheet is empty.' : 'No rows match the filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalRows > MAX_ROWS && !q.trim() && (
            <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
              Showing the first {MAX_ROWS.toLocaleString()} of {totalRows.toLocaleString()} rows. Filter to narrow it, or download the file for the rest.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
